import React, { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from 'moment-timezone';

//Controls
import { Button, Tooltip, Select } from "@gpstrackit/react-controls";
import { NotificationManager } from "Components/Notifications";
import InputDate from 'Components/InputDate';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { Icon, Chip } from '@mui/material';
import ProgressBar from "Components/ProgressBar";
import Filters from 'Components/Filters';

//Helpers
import {
    getDateFormatted,
    copyToClipboard,
    getMinDateLimitBehaviour,
    formatAddress,
    getEventColor,
    getRssiSignalIcon,
    getHDopColorLabel,
    secondsToHours
} from 'Core/data/Helpers';
import { truncateText } from 'Utils/helpers';

//Views
import DeviceReadingInfo from 'Modules/rawData/.components/tab/.components/details';

//Actions
import {
    rawDataTabSetBeforeRangeDate,
    getDeviceReadingOpenModal,
    rawDataTabSetFilters,
    clearRedux,
    openVideoPlayerModal,
    refreshMitacVideoUrlsRequest
} from 'Redux/actions';

const RANGE_MODE_ROWS = 500;

const OthersView = (props) => {
    const tableRef = useRef(null);
    const dispatch = useDispatch();
    //let endDate = new Date();
    const { companyId, messages, height, className, timezone, valuesPerPage, device, manufacturer } = props;

    //Redux States
    const { filters } = useSelector(state => state.rawDataTabRedux);
    const { openModal } = useSelector(state => state.deviceReadingRedux);
    const events = useSelector(state => state.generalRedux.events);
    const beforeRangeDateRed = useSelector(state => state.rawDataTabRedux.beforeRangeDate);
    const { loading: videoPlayerLoading } = useSelector(state => state.videoPlayerRedux);

    //Local States
    const [dateType, setDateType] = useState({ id: 'unitTime', label: messages['common_unit_time'] })
    const [dateFilters, setDateFilters] = useState(filters)

    // Helper function to extract video URLs from deviceData
    const getVideoUrlsFromDeviceData = (deviceDataString) => {
        try {
            // First parse: deviceData is a stringified JSON
            const firstParse = JSON.parse(deviceDataString);
            // Second parse: the result is still a JSON string
            const parsed = JSON.parse(firstParse);

            // Support multiple formats:
            // 1. urls as JSON string (needs additional parse)
            if (typeof parsed?.urls === 'string') {
                try {
                    const urlsArray = JSON.parse(parsed.urls);
                    if (Array.isArray(urlsArray) && urlsArray.length > 0) {
                        return urlsArray;
                    }
                } catch (e) {
                    // If parse fails, continue to other options
                }
            }
            // 2. urls as array
            if (Array.isArray(parsed?.urls) && parsed.urls.length > 0) {
                return parsed.urls;
            }
            // 3. single url string (fallback)
            if (parsed?.url) {
                return [parsed.url];
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    // Legacy function for backward compatibility
    const getVideoUrlFromDeviceData = (deviceDataString) => {
        const urls = getVideoUrlsFromDeviceData(deviceDataString);
        return urls && urls.length > 0 ? urls[0] : null;
    };

    // Helper to extract full video metadata including expiration and snapshot info
    const getVideoMetadataFromDeviceData = (deviceDataString) => {
        try {
            const firstParse = JSON.parse(deviceDataString);
            const parsed = JSON.parse(firstParse);

            let urls = null;
            if (typeof parsed?.urls === 'string') {
                try {
                    const urlsArray = JSON.parse(parsed.urls);
                    if (Array.isArray(urlsArray) && urlsArray.length > 0) {
                        urls = urlsArray;
                    }
                } catch (e) {
                    // Continue to other options
                }
            }
            if (!urls && Array.isArray(parsed?.urls) && parsed.urls.length > 0) {
                urls = parsed.urls;
            }
            if (!urls && parsed?.url) {
                urls = [parsed.url];
            }

            let fleetId = null;
            let snapshotId = null;
            if (typeof parsed?.urlMetadata === 'string') {
                try {
                    const metadata = JSON.parse(parsed.urlMetadata);
                    fleetId = metadata?.fleetId;
                    snapshotId = metadata?.snapshotId;
                } catch (e) {
                    // Continue
                }
            }

            return {
                urls,
                urlExpiration: parsed?.urlExpiration,
                fleetId,
                snapshotId
            };
        } catch (error) {
            return null;
        }
    };

    // Handler to open video player modal
    const handleOpenVideoPlayer = (item) => {
        const metadata = getVideoMetadataFromDeviceData(item?.deviceData);
        if (!metadata) {
            return;
        }

        const { urls, urlExpiration, fleetId, snapshotId } = metadata;
        const title = item?.eventName || 'Video';
        const isExpired = urlExpiration && new Date(urlExpiration).getTime() <= Date.now();

        if (isExpired && fleetId && snapshotId) {
            dispatch(refreshMitacVideoUrlsRequest({ fleetId, snapshotId, title }));
        } else if (urls && urls.length > 0) {
            dispatch(openVideoPlayerModal({ urls, title }));
        }
    };

    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Tooltip title={messages['raw_data_tab_device_reading_info']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            dispatch(getDeviceReadingOpenModal(true, { id: item?.id, deviceId: item?.deviceId }))
                        }}>
                            remove_red_eye
                        </Icon>
                    </Tooltip>
                )
            }
        },
        {
            key: 'id',
            show: true,
            title: messages["raw_data_tab_id"],
            width: 200,
            field: "id",
            export: true,
            resize: true,
            functionString: 'return row?.id',
            code: "raw_data_tab_id",
        },
        {
            key: 'sequenceNumber',
            show: true,
            title: messages["raw_data_tab_sequenceNumber"],
            width: 200,
            field: "sequenceNumber",
            export: true,
            resize: true,
            functionString: 'return row?.sequenceNumber',
            code: "raw_data_tab_sequenceNumber",
        },
        {
            key: 'accountId',
            show: true,
            title: messages["raw_data_tab_account_id"],
            width: 200,
            field: "accountId",
            export: true,
            resize: true,
            functionString: 'return row?.accountId',
            code: "raw_data_tab_account_id",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.accountId &&
                            <span style={{ fontWeight: props?.legacyId !== dataItem.accountId && 600 }}>{dataItem.accountId}</span>
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'serverTime',
            show: true,
            title: messages["raw_data_tab_server_time"],
            width: 300,
            field: "serverTime",
            export: true,
            resize: true,
            functionString: `
                function formatDateWithTimezone(date, tz) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                let timezone = '${timezone}'
                return row?.serverTime ? formatDateWithTimezone(row.serverTime, timezone) : ''
            `,
            code: "raw_data_tab_server_time",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.serverTime &&
                            getDateFormatted(dataItem.serverTime, "date", "datetime", null, timezone)
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'unitTime',
            show: true,
            title: messages["raw_data_tab_unit_time"],
            width: 300,
            field: "unitTime",
            export: true,
            resize: true,
            functionString: `
                function formatDateWithTimezone(date, tz) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                let timezone = '${timezone}'
                return row?.unitTime ? formatDateWithTimezone(row.unitTime, timezone) : ''
            `,
            code: "raw_data_tab_unit_time",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.unitTime &&
                            getDateFormatted(dataItem.unitTime, "date", "datetime", null, timezone)
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'latitude',
            show: true,
            title: messages["raw_data_tab_latitude"],
            width: 200,
            field: "latitude",
            export: true,
            resize: true,
            functionString: 'return row?.latitude',
            code: "raw_data_tab_latitude",
        },
        {
            key: 'longitude',
            show: true,
            title: messages["raw_data_tab_longitude"],
            width: 200,
            field: "longitude",
            export: true,
            resize: true,
            functionString: 'return row?.longitude',
            code: "raw_data_tab_longitude",
        },
        {
            key: 'driverName',
            show: true,
            title: messages["raw_data_tab_driver_name"],
            width: 150,
            field: "driverName",
            export: true,
            resize: true,
            functionString: 'return row?.driverName',
            code: "raw_data_tab_driver_name",
        },
        {
            key: 'eventName',
            show: true,
            title: messages["raw_data_tab_event"],
            width: 200,
            field: "eventName",
            export: true,
            resize: true,
            functionString: 'return row?.eventName',
            code: "raw_data_tab_event",
            render: (dataItem) => {
                const hasVideoUrl = manufacturer === "Mitac" && getVideoUrlFromDeviceData(dataItem?.deviceData);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {
                            dataItem.eventName &&
                            <span style={{ color: `${getEventColor(events, dataItem.eventName)}` }}>
                                {dataItem.eventName}
                            </span>
                        }
                        {
                            hasVideoUrl &&
                            <Tooltip title={messages['raw_data_tab_video_player'] || 'Play video'}>
                                <Icon
                                    style={{ cursor: videoPlayerLoading ? "not-allowed" : "pointer", fontSize: '18px', opacity: videoPlayerLoading ? 0.6 : 1 }}
                                    className="toolbar-list-icon"
                                    onClick={() => !videoPlayerLoading && handleOpenVideoPlayer(dataItem)}
                                >
                                    {videoPlayerLoading ? 'sync' : 'play_circle_filled'}
                                </Icon>
                            </Tooltip>
                        }
                    </div>
                )
            }
        },
        {
            key: 'eventCode',
            show: true,
            title: messages["raw_data_tab_raw_event"],
            width: 150,
            field: "eventCode",
            export: true,
            resize: true,
            functionString: 'return row?.eventCode',
            code: "raw_data_tab_raw_event",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            dataItem.eventCode &&
                            dataItem.eventCode
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'speed',
            show: true,
            title: messages["raw_data_tab_speed"],
            width: 150,
            field: "speed",
            export: true,
            resize: true,
            functionString: 'return row?.speed',
            code: "raw_data_tab_speed",
        },
        {
            key: 'ecuSpeed',
            show: true,
            title: messages["raw_data_tab_ecu_speed"],
            width: 150,
            field: "ecuSpeed",
            export: true,
            resize: true,
            functionString: 'return row?.ecuSpeed',
            code: "raw_data_tab_ecu_speed",
        },
        {
            key: 'formattedAddress',
            show: true,
            title: messages["raw_data_tab_gps_event_address"],
            width: 350,
            field: "formattedAddress",
            export: true,
            resize: true,
            functionString: `
                function isValidJson(str) {
                    try { JSON.parse(str) } catch (e) { return false }
                    return true
                }
                function formatAddress(element) {
                    let newAddress = element?.formattedAddress || ''
                    if (!newAddress || newAddress == 'null') {
                        if (element?.address && isValidJson(element?.address)) {
                            const { formatted } = JSON.parse(element?.address)
                            if (formatted) { newAddress = formatted || '' }
                        } else {
                            if (element?.address && element?.address?.formatted) {
                                return element.address.formatted
                            }
                            newAddress = ''
                        }
                    }
                    return newAddress
                }
                return formatAddress(row) || 'N/A'
            `,
            code: "raw_data_tab_gps_event_address",
            render: (dataItem) => {
                return (
                    <Fragment>
                        {
                            formatAddress(dataItem) || 'N/A'
                        }
                    </Fragment>
                )
            }
        },
        {
            key: 'landmarkId',
            show: true,
            title: messages["raw_data_tab_landmark_id"],
            width: 200,
            field: "landmarkId",
            export: true,
            resize: true,
            functionString: 'return row?.landmarkId',
            code: "raw_data_tab_landmark_id",
        },
        {
            key: 'satelliteCount',
            show: true,
            title: messages["raw_data_tab_satellites"],
            width: 100,
            field: "satelliteCount",
            export: true,
            resize: true,
            functionString: 'return row?.satelliteCount',
            code: "raw_data_tab_satellites",
        },
        {
            key: 'distance',
            show: true,
            title: messages['raw_data_tab_distance'],
            width: 100,
            field: "distance",
            export: true,
            resize: true,
            functionString: 'return row?.distance',
            code: "raw_data_tab_distance",
        },
        {
            key: 'hDop',
            show: true,
            title: messages['raw_data_tab_hdop'],
            width: 100,
            field: "hDop",
            export: true,
            resize: true,
            functionString: 'return row?.hDop ? (row?.hDop > 0 ? (row?.hDop/100).toFixed(2): "0") : ""',
            code: "raw_data_tab_hdop",
            render: (item) => {
                if (item?.hDop) {
                    let hdop = getHDopColorLabel(item?.hDop, messages)
                    return <Chip title={hdop.value}
                        style={{
                            marginRight: 5,
                            backgroundColor: hdop.color,
                            color: '#ffff'
                        }}
                        label={hdop.label}
                    />
                }
                return <span>{""}</span>
            }
        },
        {
            key: 'vDop',
            show: true,
            title: messages['raw_data_tab_vdop'],
            width: 100,
            field: "vDop",
            export: true,
            resize: true,
            functionString: 'return row?.vDop',
            code: "raw_data_tab_vdop",
        },
        {
            key: 'rssi',
            show: true,
            title: messages['raw_data_tab_rssi'],
            width: 100,
            export: true,
            resize: true,
            functionString: `
            function formatRssi(data) {
                let jsonData = {}
                try{
                    jsonData = data ? JSON.parse(JSON.parse(data)) : null
                }catch(exc){}
                let rssi = jsonData?.rssi || "";
                return rssi;
              }
            return row?.deviceData ? formatRssi(row?.deviceData) : ''`,
            code: "raw_data_tab_rssi",
            render: (item) => {
                let jsonData = {}
                try {
                    jsonData = item?.deviceData ? JSON.parse(JSON.parse(item?.deviceData)) : null
                } catch (exc) { }
                if (jsonData?.rssi)
                    return getRssiSignalIcon(jsonData?.rssi, messages)
                else
                    return <span>{""}</span>
            }
        },
        {
            key: 'engineHours',
            show: true,
            title: messages['raw_data_tab_engine_hours'],
            width: 150,
            export: true,
            resize: true,
            functionString: `
                function secondsToHours(seconds){
                    if (seconds == null) return 0;
                    return seconds / 3600;
                }
                let rawHours = row?.engineHours ?? 0;
                let hours = secondsToHours(rawHours);
                return Math.floor(hours * 100) / 100;
            `,
            code: "raw_data_tab_engine_hours",
            field: "engineHours",
            render: (dataItem) => {
                let rawHours = dataItem?.engineHours ?? 0;
                let hours = rawHours ? (rawHours / 3600) : 0;
                let engineHours = Math.floor(hours * 100) / 100;
                return (
                    <span>{engineHours}</span>
                )
            }
        },
        {
            key: 'engineHours_off',
            show: true,
            title: messages['raw_data_tab_engineHours_off'],
            width: 250,
            export: true,
            resize: true,
            functionString: `
                function secondsToHours(seconds){
                    if (seconds == null) return 0;
                    return seconds / 3600;
                }
                let rawHours = row?.engineHours ?? 0;
                let hours = secondsToHours(rawHours);
                let offsetEngineHours = ${device?.offsetEngineHours || 0};
                return Math.floor((hours + offsetEngineHours) * 100) / 100;
            `,
            code: "raw_data_tab_engineHours_off",
            render: (dataItem) => {
                let rawHours = dataItem?.engineHours ?? 0;
                let hours = rawHours ? (rawHours / 3600) : 0;
                let offsetEngineHours = device?.offsetEngineHours || 0;
                let value = Math.floor((hours + offsetEngineHours) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'ecuEngineHours',
            show: true,
            title: messages['raw_data_tab_ecu_engine_hours'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                let val = row?.ecuEngineHours ?? 0;
                return Math.floor(val * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engine_hours",
            field: "ecuEngineHours",
            render: (dataItem) => {
                let val = dataItem?.ecuEngineHours ?? 0;
                let ecuEngineHours = Math.floor(val * 100) / 100;
                return (
                    <span>{ecuEngineHours}</span>
                )
            }
        },
        {
            key: 'ecuEngineHours_off',
            show: true,
            title: messages['raw_data_tab_ecu_engineHours_off'],
            width: 250,
            export: true,
            resize: true,
            functionString: `
                let offsetEcuEngineHours = ${device?.offsetEcuEngineHours || 0};
                let val = row?.ecuEngineHours ?? 0;
                return Math.floor((val + offsetEcuEngineHours) * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engineHours_off",
            render: (dataItem) => {
                let offsetEcuEngineHours = device?.offsetEcuEngineHours || 0;
                let val = dataItem?.ecuEngineHours ?? 0;
                let value = Math.floor((val + offsetEcuEngineHours) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'engineOdometer',
            show: true,
            title: messages['raw_data_tab_engine_odometer'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                let val = row?.engineOdometer ?? 0;
                return Math.floor(val * 100) / 100;
            `,
            code: "raw_data_tab_engine_odometer",
            field: "engineOdometer",
            render: (dataItem) => {
                let val = dataItem?.engineOdometer ?? 0;
                let engineOdometer = Math.floor(val * 100) / 100;
                return (
                    <span>{engineOdometer}</span>
                )
            }
        },
        {
            key: 'engineOdometer_off',
            show: true,
            title: messages['raw_data_tab_engineOdometer_off'],
            width: 250,
            export: true,
            resize: true,
            functionString: `
                let offsetEngineOdometer = ${device?.offsetEngineOdometer || 0};
                let val = row?.engineOdometer ?? 0;
                return Math.floor((val + offsetEngineOdometer) * 100) / 100;
            `,
            code: "raw_data_tab_engineOdometer_off",
            render: (dataItem) => {
                let offsetEngineOdometer = device?.offsetEngineOdometer || 0;
                let val = dataItem?.engineOdometer ?? 0;
                let value = Math.floor((val + offsetEngineOdometer) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'ecuEngineOdometer',
            show: true,
            title: messages['raw_data_tab_ecu_engine_odometer'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                let val = row?.ecuEngineOdometer ?? 0;
                return Math.floor(val * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engine_odometer",
            field: "ecuEngineOdometer",
            render: (dataItem) => {
                let val = dataItem?.ecuEngineOdometer ?? 0;
                let ecuEngineOdometer = Math.floor(val * 100) / 100;
                return (
                    <span>{ecuEngineOdometer}</span>
                )
            }
        },
        {
            key: 'ecuEngineOdometer_off',
            show: true,
            title: messages['raw_data_tab_ecu_engineOdometer_off'],
            width: 250,
            export: true,
            resize: true,
            functionString: `
                let offsetEcuEngineOdometer = ${device?.offsetEcuEngineOdometer || 0};
                let val = row?.ecuEngineOdometer ?? 0;
                return Math.floor((val + offsetEcuEngineOdometer) * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engineOdometer_off",
            render: (dataItem) => {
                let offsetEcuEngineOdometer = device?.offsetEcuEngineOdometer || 0;
                let val = dataItem?.ecuEngineOdometer ?? 0;
                let value = Math.floor((val + offsetEcuEngineOdometer) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'batteryLevel',
            show: true,
            title: messages['raw_data_tab_battery_level'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                function getBatteryPercent(deviceData) {
                    try {
                        return deviceData ? (JSON.parse(JSON.parse(deviceData)).batteryPercent || "") : ""
                    } catch (error) {
                        return ""
                    }
                }
                return getBatteryPercent(row?.deviceData)
            `,
            code: "raw_data_tab_battery_level",
            render: (dataItem) => {
                let battery = null
                try {
                    battery = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem.deviceData)).batteryPercent ? JSON.parse(JSON.parse(dataItem.deviceData)).batteryPercent : null : null
                } catch (error) { }
                let color = "green"
                if (battery && battery < 10) {
                    color = "red"
                } else if (battery && battery < 20) {
                    color = "yellow"
                }
                return <span>{battery && <ProgressBar value={battery} color={color} />}</span>
            }
        },
        {
            key: 'rawMessage',
            show: true,
            title: messages["raw_data_tab_raw"],
            width: 400,
            field: "rawMessage",
            export: true,
            resize: true,
            functionString: 'return row?.rawMessage',
            code: "raw_data_tab_raw",
            render: (dataItem) => {
                return (
                    <Fragment>
                        <td>
                            {
                                dataItem.rawMessage &&
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span className="cursor-pointer pr-1"
                                        onClick={() => {
                                            props.showModal(dataItem.rawMessage)
                                        }}
                                    >
                                        <Icon>
                                            search
                                        </Icon>
                                    </span>
                                    <span className="cursor-pointer pr-1"
                                        onClick={() => {
                                            copyToClipboard(dataItem.rawMessage, messages['common_copied'])
                                        }}
                                    >
                                        <Icon style={{ fontSize: 19, margin: '0 3px 0px 0' }}>
                                            content_copy
                                        </Icon>
                                    </span>
                                    <span title={dataItem.rawMessage.length > 200 ? "Click search icon to view full message" : undefined}>
                                        {truncateText(dataItem.rawMessage, 200)}
                                    </span>
                                </div>
                            }
                        </td>
                    </Fragment>
                )
            }
        },
    ])

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(tempStartDate);
    const [endDate, setEndDate] = useState(new Date());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })
    const [last, setLast] = useState(filters?.last || { id: '20', name: '20' });
    const [rows, setRows] = useState(500);
    const [type, setType] = useState('last');

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);
        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range

        const newDateRange = {
            start,
            end,
        };
        dispatch(rawDataTabSetBeforeRangeDate(newDateRange))

        dispatch(rawDataTabSetFilters({ ...fltrs }))
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDateRed;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            dispatch(rawDataTabSetFilters({ ...fltrs }))
        }
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    const handleOrderType = (dateType) => {
        setDateType(dateType)
    }

    const applyRangeFilter = useCallback((dateType, startDate, endDate) => {
        return {
            fields: [dateType],
            GTE: moment.tz(moment(startDate).format("YYYY-MM-DD") + " 00:00:00", timezone)
                .utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
            LTE: moment.tz(moment(endDate).format("YYYY-MM-DD") + " 23:59:59", timezone)
                .utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        }
    }, [timezone])

    const applyFilters = (paramType) => {
        let currentNewConditions = tableRef?.current?.conditions;
        let newType = paramType ? paramType : type
        // Remove existing deviceId and accountId conditions to avoid duplicates
        currentNewConditions = currentNewConditions?.filter((item) => item?.fields?.[0] !== 'deviceId' && item?.fields?.[0] !== 'accountId') || [];
        if (newType !== "last" && dateFilters.startDate && dateFilters.endDate) {
            currentNewConditions && currentNewConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                range: applyRangeFilter(dateType?.id, dateFilters.startDate, dateFilters.endDate),
                conditionOperator: "AND"
            });

        } else if (newType !== "last") {

            NotificationManager.warning(
                messages['raw_data_tab_date_range'],
                messages['raw_data_tab_raw_data_modal_title'],
                5000,
            );
            return
        } else {
            currentNewConditions && currentNewConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                conditionOperator: "AND"
            });
        }

        currentNewConditions && currentNewConditions.push({
            fields: ["accountId"],
            terms: [companyId],
            exact: true,
            conditionOperator: "AND"
        });

        let current = { key: dateType.id, order: "DESC" }

        if (dateType.id === "serverTime") {
            current = [{ field: "serverTime", order: "DESC" }, { field: "unitTime", order: "DESC" }]
        }
        
        dispatch(rawDataTabSetFilters({ ...dateFilters, last: last, type: newType }))
        //execute the account search
        tableRef?.current?.getData({
            newRows: newType === 'last' ? parseInt(last.id, 10) : rows || null,
            newSort: current,
            newConditions: currentNewConditions,
            showTotalCount: newType !== 'last',
        });
        tableRef?.current?.setRows(newType === 'last' ? last.id : rows);

    }

    useEffect(() => {
        if (filters) {
            setDateFilters(filters)
            setType(filters.type)
            setLast(filters.last)

            setStartDate(filters.startDate)
            setEndDate(filters.endDate)
        }
    }, [filters])

    /**
     * Synchronizes date/last filters from DataView condition changes.
     */
    const handleChangeConditions = (tags, conditions, total, newColumns, rowsPeerPage, sort) => {
        let currentConditions = {}
        currentConditions = conditions || [];
        let currentDateType = dateType.id
        let currentLast = last
        let currentType = type
        let currentDate = conditions.find((item) => item?.range?.fields)
        let fltrs = filters;
        if (currentDate) {
            currentDateType = currentDate?.range?.fields[0]
        }
        setDateType({ id: currentDateType, label: currentDateType === 'serverTime' ? messages['common_server_time'] : messages['common_unit_time'] })

        let currentSearchOption = conditions.find((item) => item?.range)

        currentConditions = currentConditions?.filter((item) => item?.fields?.[0] !== 'deviceId' && item?.fields?.[0] !== 'accountId');

        if (currentConditions && currentSearchOption) {
            currentConditions && currentConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                range: applyRangeFilter(currentDateType, dateFilters.startDate, dateFilters.endDate),
                conditionOperator: "AND"
            });
            setType('range')
            currentType = 'range'
            setRows(rows || 500)
            tableRef?.current.setRows(rows || 500)

        } else if (currentConditions && !currentSearchOption) {
            currentConditions && currentConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                conditionOperator: "AND"
            });
            setType("last")
            currentType = 'last'
        }
        if (rowsPeerPage && currentType === "last") {
            setLast({ id: `${rowsPeerPage}`, name: `${rowsPeerPage}` })
            currentLast = { id: `${rowsPeerPage}`, name: `${rowsPeerPage}` }
        }
        let current = { key: currentDateType, order: "DESC" }
        if (currentDateType === "serverTime") {
            current = [{ field: "serverTime", order: "DESC" }, { field: "unitTime", order: "DESC" }]
        }

        tableRef?.current?.conditionsHandler(currentConditions);
        tableRef?.current.setSort(current) 
        dispatch(rawDataTabSetFilters({ ...fltrs, last: currentLast, type: currentType, companyId: companyId }))
    }

    /**
     * Renders custom date/last filtering controls for raw data.
     */
    const renderExtraFilters = () => {
        return (
            <div style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Filters
                    onChangeFilters={() => applyFilters()}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setBeforeRangeDate={setBeforeRangeDate}
                    beforeRangeDate={beforeRangeDate}
                    onChange={onChange}
                    onChangeStartEndDate={onChangeStartEndDate}
                    onCloseSelectDate={onCloseSelectDate}
                    last={last}
                    setLast={setLast}
                    dateType={dateType}
                    handleOrderType={handleOrderType}
                    type={type}
                    setType={(value) => {
                        setType(value)
                        if (value === 'range') {
                            tableRef?.current?.setRows(RANGE_MODE_ROWS);
                        }
                        dispatch(clearRedux("COMMON", {
                            'device-readings-others': {}
                        }))
                        applyFilters(value)
                    }}
                />
            </div>
        )
    }

    /**
     * Triggers refresh using current last/range filters.
     */
    const handleRefresh = () => {
        applyFilters()
    }

    return (
        <div className="raw-data-others-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                key="pm_raw_data_other"
                ref={tableRef}
                graphqlConfig={{
                    function: "searchDeviceReadings",
                    sortType: "DeviceReadingSortInput",
                    conditionsType: "DeviceReadingSearchInput",
                    customQuery: "id deviceId deviceData",
                    dynamicQuery: true,
                }}
                searchConfig={{
                    disabled: true,
                }}
                uiConfig={{
                    hideFilters: true,
                    hideSplit: true,
                    hidePagination: type === "last",
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "600px",
                    buttonsFlexWrap: false,
                }}
                selectionConfig={{
                    disabled: true,
                }}
                exportConfig={{
                    fileName: "rawdata",
                    disablePDF: true,
                    disableExcel: true,
                }}
                defaultsConfig={{
                    sort: { field: dateType?.id, order: "DESC" },
                    rowsPerPage: 50,
                    valuesPerPage: valuesPerPage,
                }}
                virtualizationConfig={{
                    overscan: 100,
                }}
                name="raw-data-others-v10"
                entity="device-readings-others"
                columns={columns}
                messages={messages}
                onChangeConditions={handleChangeConditions}
                externalRefresh={handleRefresh}
                extraFilters={renderExtraFilters}
                onChangeColumns={(items) => setColumns(items)}
                tableButtons={["export", 'orderColumn']}
                infiniteScroll={type !== 'last'}
                showTotalCount={type !== 'last'}
                onChangeRowsPerPage={(rows) => {
                    setRows(rows)
                    tableRef?.current?.getData({ newRows: rows });
                }}
            />
            {openModal && <DeviceReadingInfo />}
        </div>
    )

}

export default OthersView;
